import {
  LIVE_UPDATE_FAILURE_CATEGORIES,
  type LiveUpdateCoordination,
  type LiveUpdateMode,
} from "./diagnostics";
import {
  parseLiveUpdateMessage,
  type LiveUpdateEvent,
} from "./liveUpdateMessage";
import {
  LIVE_UPDATE_TRANSPORT_STATES,
  type LiveUpdateTransport,
  type TransportStateChange,
} from "./socketIoTransport";

const HEARTBEAT_MS = 5_000;
const OWNER_LIVENESS_MS = 15_000;
const FROZEN_OWNER_FALLBACK_MS = 90_000;
export const LIVE_UPDATE_DISCONNECT_GRACE_MS = 5_000;

type CrossTabMessage =
  | { kind: "presence"; tabId: string; activeCount: number; sentAt: number }
  | { kind: "leaving"; tabId: string }
  | { kind: "request-presence" }
  | {
      kind: "owner-heartbeat";
      tabId: string;
      sentAt: number;
      transport: TransportStateChange | null;
    }
  | { kind: "event"; event: LiveUpdateEvent }
  | { kind: "reconcile"; tabId?: string }
  | { kind: "retry" }
  | { kind: "force-disconnect" }
  | { kind: "reconnect" };

interface CrossTabCoordinatorOptions {
  identity: string;
  transport: LiveUpdateTransport;
  onEvent(event: LiveUpdateEvent): void;
  onReconnect(): void;
  onStateChange(change: TransportStateChange): void;
  onCoordinationChange?(coordination: LiveUpdateCoordination): void;
  onMalformedMessage(): void;
  now?: () => number;
}

export interface CrossTabCoordinator {
  start(): void;
  setActiveCount(count: number): void;
  retry(): void;
  forceDisconnect(): void;
  reconnect(): void;
  dispose(): void;
}

interface PeerPresence {
  activeCount: number;
  lastSeenAt: number;
}

/** Produces a deterministic key without putting session identifiers in browser names. */
export const opaqueConnectionKey = (identity: string): string => {
  let first = 0x811c9dc5;
  let second = 0x9e3779b9;

  for (let index = 0; index < identity.length; index += 1) {
    const character = identity.charCodeAt(index);
    first = Math.imul(first ^ character, 0x01000193);
    second = Math.imul(second ^ character, 0x85ebca6b);
  }

  return `${(first >>> 0).toString(16).padStart(8, "0")}${(second >>> 0)
    .toString(16)
    .padStart(8, "0")}`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const TRANSPORT_STATES = new Set<string>(LIVE_UPDATE_TRANSPORT_STATES);
const FAILURE_CATEGORIES = new Set<string>(LIVE_UPDATE_FAILURE_CATEGORIES);

const isCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

/** Rebuilds a relayed state from validated fields only; extra channel input is dropped. */
const parseTransportState = (value: unknown): TransportStateChange | null => {
  if (
    !isRecord(value) ||
    typeof value.state !== "string" ||
    !TRANSPORT_STATES.has(value.state)
  ) {
    return null;
  }
  const { state, failureCategory, retryAttempt, tokenExpiresAt } = value;
  if (
    failureCategory !== null &&
    (typeof failureCategory !== "string" ||
      !FAILURE_CATEGORIES.has(failureCategory))
  ) {
    return null;
  }
  if (retryAttempt !== undefined && !isCount(retryAttempt)) return null;
  if (
    tokenExpiresAt !== undefined &&
    tokenExpiresAt !== null &&
    !(typeof tokenExpiresAt === "number" && Number.isFinite(tokenExpiresAt))
  ) {
    return null;
  }
  return {
    state: state as TransportStateChange["state"],
    failureCategory: failureCategory as TransportStateChange["failureCategory"],
    ...(retryAttempt === undefined ? {} : { retryAttempt }),
    ...(tokenExpiresAt === undefined ? {} : { tokenExpiresAt }),
  };
};

const createTabId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** Elects one socket owner and relays safe transport output to equivalent tabs. */
export const createCrossTabCoordinator = ({
  identity,
  transport,
  onEvent,
  onReconnect,
  onStateChange,
  onCoordinationChange,
  onMalformedMessage,
  now = Date.now,
}: CrossTabCoordinatorOptions): CrossTabCoordinator => {
  const tabId = createTabId();
  const key = opaqueConnectionKey(identity);
  const lockName = `portal-live-updates:v1:${key}`;
  const locksSupported =
    typeof navigator !== "undefined" &&
    navigator.locks !== undefined &&
    typeof navigator.locks.request === "function" &&
    typeof navigator.locks.query === "function";
  let channel: BroadcastChannel | null = null;
  if (locksSupported && typeof BroadcastChannel !== "undefined") {
    try {
      channel = new BroadcastChannel(`portal-live-updates:v1:${key}`);
    } catch {
      channel = null;
    }
  }
  let sharingSupported = channel !== null && locksSupported;
  const peers = new Map<string, PeerPresence>();

  let activeCount = 0;
  let disposed = false;
  let started = false;
  let owner = false;
  let fallbackActive = false;
  let transportRunning = false;
  let needsInitialRefresh = false;
  let lastOwnerActivityAt: number | null = null;
  let ownerWaitStartedAt: number | null = null;
  let lastTransportState: TransportStateChange | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let disconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let releaseOwnership: (() => void) | null = null;
  let ownershipRequest: AbortController | null = null;
  let livenessCheckInFlight = false;
  let coordination: LiveUpdateCoordination | null = onCoordinationChange
    ? {
        mode: "inactive",
        sharingSupport: !locksSupported
          ? "no-locks"
          : channel === null
            ? "no-broadcast-channel"
            : "supported",
        tabId,
        peerCount: null,
      }
    : null;

  const publishCoordination = (
    patch: Partial<LiveUpdateCoordination>,
  ): void => {
    if (!coordination || !onCoordinationChange) return;
    const next = { ...coordination, ...patch };
    if (
      next.mode === coordination.mode &&
      next.sharingSupport === coordination.sharingSupport &&
      next.peerCount === coordination.peerCount
    ) {
      return;
    }
    coordination = next;
    onCoordinationChange(coordination);
  };

  const setMode = (mode: LiveUpdateMode): void => publishCoordination({ mode });

  const syncPeerCount = (): void =>
    publishCoordination({
      peerCount: owner ? Math.max(0, peers.size - 1) : null,
    });

  const post = (message: CrossTabMessage): void =>
    channel?.postMessage(message);

  const stopDisconnectTimer = (): void => {
    if (disconnectTimer) clearTimeout(disconnectTimer);
    disconnectTimer = null;
  };

  const stopHeartbeat = (): void => {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  };

  const publishPresence = (): void => {
    const sentAt = now();
    if (owner) peers.set(tabId, { activeCount, lastSeenAt: sentAt });
    post({ kind: "presence", tabId, activeCount, sentAt });
  };

  const publishOwnerHeartbeat = (): void => {
    if (!owner) return;
    post({
      kind: "owner-heartbeat",
      tabId,
      sentAt: now(),
      transport: lastTransportState,
    });
  };

  const reconcileSubscriptions = (): void => {
    onReconnect();
    if (owner) post({ kind: "reconcile" });
  };

  const transportConsumer = {
    onEvent: (event: LiveUpdateEvent): void => {
      onEvent(event);
      if (owner) post({ kind: "event", event });
    },
    onReconnect: reconcileSubscriptions,
    onStateChange: (change: TransportStateChange): void => {
      lastTransportState = change;
      onStateChange(change);
      if (owner) publishOwnerHeartbeat();
      // Both ownership and fallback start a fresh transport. It must refresh on
      // its first connection; subsequent connections use onReconnect above.
      if (change.state === "connected" && needsInitialRefresh) {
        needsInitialRefresh = false;
        reconcileSubscriptions();
      }
    },
    onMalformedMessage,
  };

  const totalActiveCount = (): number =>
    [...peers.values()].reduce((total, peer) => total + peer.activeCount, 0);

  const startTransport = (): void => {
    if (transportRunning) return;
    transportRunning = true;
    needsInitialRefresh = true;
    transport.start(transportConsumer);
  };

  const stopTransport = (): void => {
    stopDisconnectTimer();
    transport.stop();
    transportRunning = false;
    needsInitialRefresh = false;
    lastTransportState = null;
  };

  const syncOwnerTransport = (): void => {
    if (!owner || disposed) return;
    syncPeerCount();
    const needed = totalActiveCount() > 0;

    if (needed) {
      stopDisconnectTimer();
      startTransport();
      return;
    }

    if (!disconnectTimer) {
      disconnectTimer = setTimeout(() => {
        disconnectTimer = null;
        if (owner && totalActiveCount() === 0) {
          stopOwning();
          releaseOwnership?.();
          releaseOwnership = null;
        }
      }, LIVE_UPDATE_DISCONNECT_GRACE_MS);
    }
  };

  const startFallback = (): void => {
    if (fallbackActive || disposed || activeCount === 0) return;
    fallbackActive = true;
    setMode("per-tab");
    startTransport();
  };

  const stopFallback = (reconcile: boolean): void => {
    if (!fallbackActive) return;
    fallbackActive = false;
    stopTransport();
    if (reconcile && activeCount > 0) onReconnect();
  };

  const becomeOwner = (): void => {
    stopFallback(false);
    owner = true;
    ownerWaitStartedAt = null;
    setMode("owner");
    peers.set(tabId, { activeCount, lastSeenAt: now() });
    post({ kind: "request-presence" });
    syncOwnerTransport();
    publishOwnerHeartbeat();
  };

  const stopOwning = (): void => {
    if (!owner) return;
    owner = false;
    peers.clear();
    syncPeerCount();
    stopTransport();
    if (activeCount === 0) {
      stopHeartbeat();
      setMode("inactive");
    }
  };

  const recordOwnerActivity = (): void => {
    lastOwnerActivityAt = now();
    ownerWaitStartedAt = null;
    if (fallbackActive) {
      stopFallback(true);
      setMode("follower");
    }
  };

  const expirePeers = (): void => {
    const cutoff = now() - OWNER_LIVENESS_MS;
    peers.forEach((peer, peerTabId) => {
      if (peerTabId !== tabId && peer.lastSeenAt < cutoff)
        peers.delete(peerTabId);
    });
    syncOwnerTransport();
  };

  const checkOwnerLiveness = (): void => {
    if (owner || activeCount === 0 || fallbackActive || livenessCheckInFlight) {
      return;
    }
    const lastActivity = lastOwnerActivityAt ?? ownerWaitStartedAt;
    if (lastActivity === null || now() - lastActivity < OWNER_LIVENESS_MS)
      return;

    livenessCheckInFlight = true;
    void navigator.locks
      .query()
      .then((snapshot) => {
        if (disposed || owner || activeCount === 0 || fallbackActive) return;
        const ownerLockHeld = snapshot.held?.some(
          (lock) => lock.name === lockName,
        );
        // A frozen tab can retain its lock while missing heartbeats. Give it a
        // longer grace period, then open a fallback without stealing the lock.
        if (ownerLockHeld && now() - lastActivity < FROZEN_OWNER_FALLBACK_MS) {
          return;
        }
        startFallback();
      })
      .catch(() => {
        if (!disposed && !owner && activeCount > 0) startFallback();
      })
      .finally(() => {
        livenessCheckInFlight = false;
      });
  };

  const handleMessage = (messageEvent: MessageEvent<unknown>): void => {
    if (!started || disposed || (!owner && activeCount === 0)) return;
    const message = messageEvent.data;
    if (!isRecord(message) || typeof message.kind !== "string") return;

    switch (message.kind) {
      case "presence":
        if (
          owner &&
          typeof message.tabId === "string" &&
          typeof message.activeCount === "number" &&
          Number.isSafeInteger(message.activeCount) &&
          message.activeCount >= 0 &&
          typeof message.sentAt === "number"
        ) {
          const needsSnapshot =
            message.activeCount > 0 &&
            !peers.get(message.tabId)?.activeCount &&
            lastTransportState?.state === "connected";
          peers.set(message.tabId, {
            activeCount: message.activeCount,
            lastSeenAt: message.sentAt,
          });
          syncOwnerTransport();
          if (needsSnapshot) {
            // Broadcasts are not replayed. A newly active follower must fetch
            // changes that happened while it had no subscribed page.
            publishOwnerHeartbeat();
            post({ kind: "reconcile", tabId: message.tabId });
          }
        }
        break;
      case "leaving":
        if (owner && typeof message.tabId === "string") {
          peers.delete(message.tabId);
          syncOwnerTransport();
        }
        break;
      case "request-presence":
        publishPresence();
        break;
      case "owner-heartbeat":
        if (!owner && typeof message.sentAt === "number") {
          recordOwnerActivity();
          setMode("follower");
          const transportState = parseTransportState(message.transport);
          if (transportState) onStateChange(transportState);
        }
        break;
      case "event": {
        if (owner) break;
        const event = parseLiveUpdateMessage(message.event);
        if (!event) {
          onMalformedMessage();
          break;
        }
        recordOwnerActivity();
        onEvent(event);
        break;
      }
      case "reconcile":
        if (
          !owner &&
          (message.tabId === undefined || message.tabId === tabId)
        ) {
          recordOwnerActivity();
          onReconnect();
        }
        break;
      case "retry":
        if (owner) transport.retry();
        break;
      case "force-disconnect":
        if (owner) transport.forceDisconnect();
        break;
      case "reconnect":
        if (owner) transport.reconnect();
        break;
    }
  };

  if (channel) channel.onmessage = handleMessage;

  const requestOwnership = (): void => {
    if (
      disposed ||
      !sharingSupported ||
      owner ||
      ownershipRequest ||
      activeCount === 0
    )
      return;
    const controller = new AbortController();
    ownershipRequest = controller;
    void navigator.locks
      .request(lockName, { signal: controller.signal }, async () => {
        if (disposed || activeCount === 0) return;
        // Web Locks holds ownership until this callback resolves. Releasing this
        // promise lets the next queued tab take over after idle grace or disposal.
        await new Promise<void>((resolve) => {
          releaseOwnership = resolve;
          becomeOwner();
        });
        stopOwning();
      })
      .catch(() => {
        if (disposed || controller.signal.aborted) return;
        sharingSupported = false;
        stopHeartbeat();
        channel?.close();
        channel = null;
        publishCoordination({ mode: "per-tab", sharingSupport: "lock-error" });
        if (activeCount > 0) startFallback();
      })
      .finally(() => {
        if (ownershipRequest === controller) ownershipRequest = null;
        requestOwnership();
      });
  };

  const syncActivity = (): void => {
    if (!started || disposed) return;
    if (activeCount > 0) stopDisconnectTimer();

    if (sharingSupported) {
      if (activeCount > 0) {
        if (!owner && !fallbackActive) setMode("follower");
        heartbeatTimer ??= setInterval(() => {
          publishPresence();
          if (owner) {
            expirePeers();
            publishOwnerHeartbeat();
          } else {
            checkOwnerLiveness();
          }
        }, HEARTBEAT_MS);
        requestOwnership();
      } else if (!owner) {
        ownershipRequest?.abort();
        stopHeartbeat();
        setMode("inactive");
      }
      publishPresence();
      if (owner) syncOwnerTransport();
    } else if (activeCount > 0) {
      startFallback();
    }

    // A frozen-owner fallback still has browser sharing support.
    if (activeCount === 0 && fallbackActive && !disconnectTimer) {
      disconnectTimer = setTimeout(() => {
        disconnectTimer = null;
        if (activeCount === 0) {
          stopFallback(false);
          setMode("inactive");
        }
      }, LIVE_UPDATE_DISCONNECT_GRACE_MS);
    }
  };

  return {
    start: () => {
      if (started || disposed) return;
      started = true;
      syncActivity();
    },
    setActiveCount: (count) => {
      const wasInactive = activeCount === 0;
      activeCount = Math.max(0, Math.floor(count));
      if (wasInactive && activeCount > 0 && !owner) {
        ownerWaitStartedAt = now();
        lastOwnerActivityAt = null;
      }
      if (activeCount === 0) ownerWaitStartedAt = null;
      syncActivity();
    },
    retry: () => {
      if (owner || fallbackActive) transport.retry();
      else post({ kind: "retry" });
    },
    forceDisconnect: () => {
      if (owner || fallbackActive) transport.forceDisconnect();
      else post({ kind: "force-disconnect" });
    },
    reconnect: () => {
      if (owner || fallbackActive) transport.reconnect();
      else post({ kind: "reconnect" });
    },
    dispose: () => {
      if (disposed) return;
      disposed = true;
      post({ kind: "leaving", tabId });
      stopHeartbeat();
      ownershipRequest?.abort();
      stopDisconnectTimer();
      if (fallbackActive) stopFallback(false);
      releaseOwnership?.();
      releaseOwnership = null;
      if (owner) stopOwning();
      channel?.close();
      transport.dispose();
      publishCoordination({ mode: "inactive", peerCount: null });
    },
  };
};
